import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  OnInit,
  computed,
  inject,
  input,
  signal,
} from '@angular/core';
import {
  AbstractControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { combineLatest, startWith } from 'rxjs';
import moment from 'moment';
import {
  RequestScheduleType,
  ScheduleRecurrenceBy,
} from '../../../../../common/models/request-schedule.model';
import {
  EVENT_MONTHLY_BY_OPTIONS,
  EVENT_MONTHS,
  EVENT_WEEK_DAYS,
} from '../../../../../common/constants/recurrence.constants';
import {
  RepeatOnOptionValue,
  buildMonthlyRepeatOnOptions,
  buildYearlyRepeatOnOptions,
} from '../../../../../common/utils/recurrence.utils';
import { InputComponent } from '../../../../../../ui-components/lib/input/input.component';
import { MultiSelectComponent } from '../../../../../../ui-components/lib/multi-select/multi-select.component';
import { SelectComponent } from '../../../../../../ui-components/lib/select/select.component';
import { ValidationMessageComponent } from '../../../../../../ui-components/lib/validation-message/validation-message.component';
import { DateFormat } from '../../../../../../ui-components/lib/date-picker/date-picker.constant';

@Component({
  selector: 'lib-schedule-recurrence-fields',
  imports: [
    ReactiveFormsModule,
    TranslocoPipe,
    SelectComponent,
    MultiSelectComponent,
    InputComponent,
    ValidationMessageComponent,
  ],
  templateUrl: './schedule-recurrence-fields.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'contents',
  },
})
export class ScheduleRecurrenceFieldsComponent implements OnInit {
  private readonly transloco = inject(TranslocoService);
  private readonly destroyRef = inject(DestroyRef);

  readonly form = input.required<FormGroup>();

  private readonly scheduleTypeState = signal<string>('');
  private readonly recurrenceByState = signal<string>(
    ScheduleRecurrenceBy.INTERVAL
  );
  private readonly startAtState = signal<string | null>(null);

  readonly isOneTime = computed(() => {
    const type = this.scheduleTypeState();
    return type === RequestScheduleType.ONE_TIME || !type;
  });

  readonly isWeekly = computed(
    () => this.scheduleTypeState() === RequestScheduleType.WEEKLY
  );

  readonly isMonthly = computed(
    () => this.scheduleTypeState() === RequestScheduleType.MONTHLY
  );

  readonly isYearly = computed(
    () => this.scheduleTypeState() === RequestScheduleType.YEARLY
  );

  readonly isMonthlyByInterval = computed(
    () =>
      this.isMonthly() &&
      this.recurrenceByState() === ScheduleRecurrenceBy.INTERVAL
  );

  readonly isMonthlyByMonths = computed(
    () =>
      this.isMonthly() &&
      this.recurrenceByState() === ScheduleRecurrenceBy.MONTHS
  );

  readonly showInterval = computed(
    () => !this.isOneTime() && (!this.isMonthly() || this.isMonthlyByInterval())
  );

  readonly recurrenceByOptions = computed(() =>
    EVENT_MONTHLY_BY_OPTIONS.map(option => ({
      value: option.value,
      label: this.transloco.translate(option.label),
    }))
  );

  readonly translatedWeekDayOptions = computed(() =>
    EVENT_WEEK_DAYS.map(day => ({
      value: day.value,
      label: this.transloco.translate(
        `schedule_workflow_week_day_${day.label}_label`
      ),
    }))
  );

  readonly translatedMonthOptions = computed(() =>
    EVENT_MONTHS.map(month => ({
      value: month.value,
      label: this.transloco.translate(
        `schedule_workflow_month_${month.label}_label`
      ),
    }))
  );

  readonly repeatEveryUnit = computed(() => {
    const unitMap: Record<string, string> = {
      [RequestScheduleType.DAILY]: this.transloco.translate(
        'schedule_workflow_unit_days'
      ),
      [RequestScheduleType.WEEKLY]: this.transloco.translate(
        'schedule_workflow_unit_weeks'
      ),
      [RequestScheduleType.MONTHLY]: this.transloco.translate(
        'schedule_workflow_unit_months'
      ),
      [RequestScheduleType.YEARLY]: this.transloco.translate(
        'schedule_workflow_unit_years'
      ),
    };

    return unitMap[this.scheduleTypeState()] || '';
  });

  readonly repeatOnOptions = computed(() => {
    const startAt = this.startAtState();
    const type = this.scheduleTypeState();

    if (!startAt) {
      return [] as { label: string; value: RepeatOnOptionValue }[];
    }

    const startDate = this.parseFormDateToDate(startAt);
    if (!startDate) {
      return [] as { label: string; value: RepeatOnOptionValue }[];
    }

    const translate = (key: string, params?: Record<string, string | number>) =>
      this.transloco.translate(key, params);

    if (type === RequestScheduleType.MONTHLY) {
      return buildMonthlyRepeatOnOptions(startDate, translate);
    }

    if (type === RequestScheduleType.YEARLY) {
      return buildYearlyRepeatOnOptions(startDate, translate);
    }

    return [] as { label: string; value: RepeatOnOptionValue }[];
  });

  ngOnInit(): void {
    this.bindRecurrenceFormState();
  }

  private bindRecurrenceFormState(): void {
    const form = this.form();
    const recurrenceGroup = form.controls['recurrence'] as FormGroup;
    const scheduleTypeControl = form.controls['schedule_type'];
    const startAtControl = form.controls['start_at'];
    const byControl = recurrenceGroup?.controls?.['by'];

    if (
      !recurrenceGroup ||
      !scheduleTypeControl ||
      !startAtControl ||
      !byControl
    ) {
      return;
    }

    combineLatest({
      type: scheduleTypeControl.valueChanges.pipe(
        startWith(scheduleTypeControl.value ?? '')
      ),
      by: byControl.valueChanges.pipe(
        startWith(byControl.value ?? ScheduleRecurrenceBy.INTERVAL)
      ),
      startAt: startAtControl.valueChanges.pipe(
        startWith(startAtControl.value ?? null)
      ),
    })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(({ type, by, startAt }) => {
        const nextType = type ?? '';
        const nextBy = by ?? ScheduleRecurrenceBy.INTERVAL;

        this.scheduleTypeState.set(nextType);
        this.recurrenceByState.set(nextBy);
        this.startAtState.set(startAt ?? null);
        this.applyRecurrenceValidators(nextType, nextBy);
      });
  }

  private applyRecurrenceValidators(type: string, by: string) {
    const recurrenceGroup = this.form().controls['recurrence'] as FormGroup;
    const intervalControl = recurrenceGroup.controls['interval'];
    const daysControl = recurrenceGroup.controls['days_of_week'];
    const byControl = recurrenceGroup.controls['by'];
    const monthsControl = recurrenceGroup.controls['months'];
    const repeatOnControl = recurrenceGroup.controls['repeatOnByDayOfMonth'];

    const isRecurring = !!type && type !== RequestScheduleType.ONE_TIME;
    const isMonthly = type === RequestScheduleType.MONTHLY;
    const isYearly = type === RequestScheduleType.YEARLY;
    const isMonthlyByMonths = isMonthly && by === ScheduleRecurrenceBy.MONTHS;
    const showInterval =
      isRecurring && (!isMonthly || by === ScheduleRecurrenceBy.INTERVAL);

    intervalControl.setValidators(
      showInterval ? [Validators.required, Validators.min(1)] : []
    );

    daysControl.setValidators(
      type === RequestScheduleType.WEEKLY ? [this.arrayRequiredValidator] : []
    );

    byControl.setValidators(isMonthly ? [Validators.required] : []);

    repeatOnControl.setValidators(
      isMonthly || isYearly ? [Validators.required] : []
    );

    monthsControl.setValidators(
      isMonthlyByMonths ? [this.arrayRequiredValidator] : []
    );

    if (!isRecurring) {
      intervalControl.setValue(null, { emitEvent: false });
    }

    if (type !== RequestScheduleType.WEEKLY) {
      daysControl.setValue([], { emitEvent: false });
    }

    if (!isMonthly && !isYearly) {
      repeatOnControl.setValue(null, { emitEvent: false });
    }

    if (isMonthlyByMonths) {
      intervalControl.setValue(1, { emitEvent: false });
    } else {
      monthsControl.setValue([], { emitEvent: false });
    }

    intervalControl.updateValueAndValidity({ emitEvent: false });
    daysControl.updateValueAndValidity({ emitEvent: false });
    byControl.updateValueAndValidity({ emitEvent: false });
    monthsControl.updateValueAndValidity({ emitEvent: false });
    repeatOnControl.updateValueAndValidity({ emitEvent: false });

    // `by` only applies to monthly, and the reset has to be emitted so the
    // derived state stays in sync when the type is switched back to monthly.
    if (!isMonthly && byControl.value !== ScheduleRecurrenceBy.INTERVAL) {
      byControl.setValue(ScheduleRecurrenceBy.INTERVAL);
    }
  }

  private parseFormDateToDate(value: string): Date | undefined {
    const dateValue = value.includes(' - (')
      ? value.split(' - (')[0].trim()
      : value.trim();

    const parsed = moment.utc(dateValue, DateFormat, true);
    if (parsed.isValid()) {
      return parsed.toDate();
    }

    const fallback = moment(dateValue);
    return fallback.isValid() ? fallback.toDate() : undefined;
  }

  private arrayRequiredValidator(control: AbstractControl) {
    const value = control.value as unknown[] | null;
    return value?.length ? null : { required: true };
  }
}
